import type { DuplicateCandidateStatus, DuplicateListResponse, DuplicatePairDTO, DuplicateScanStatusDTO } from '@hearth/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, ScanSearch } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';
import { RelativeTime } from '../../components/crm';
import { Page } from '../../components/layout/Page';
import { PageHeader } from '../../components/layout/PageHeader';
import { Badge, Button, Card, EmptyState, Skeleton, Tabs, useToast } from '../../components/ui';
import { useAuth } from '../../hooks/useAuth';
import { api, errorMessage } from '../../lib/api';

const STATUS_TABS: { key: DuplicateCandidateStatus; label: string }[] = [
  { key: 'pending', label: 'To review' },
  { key: 'merged', label: 'Merged' },
  { key: 'kept_separate', label: 'Kept separate' },
  { key: 'ignored', label: 'Ignored' },
];

export function confidenceColor(confidence: number): string {
  return confidence >= 85 ? 'red' : confidence >= 70 ? 'amber' : 'slate';
}

function PairSide({ side }: { side: DuplicatePairDTO['a'] }) {
  return (
    <div className="min-w-0">
      <p className="truncate font-medium text-slate-900">{side.name}</p>
      <p className="truncate text-xs text-slate-500">{[side.addressLine1, side.city].filter(Boolean).join(', ') || 'No address'}</p>
      <p className="truncate text-xs text-slate-500">
        {[side.phone, side.website?.replace(/^https?:\/\//, '')].filter(Boolean).join(' · ') || '—'} · {side.contactsCount} contacts · {side.activitiesCount} activities
      </p>
    </div>
  );
}

export function DuplicatesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<DuplicateCandidateStatus>('pending');
  const { data, isLoading } = useQuery({
    queryKey: ['duplicates', 'list', status],
    queryFn: ({ signal }) => api.get<DuplicateListResponse>(`/api/duplicates?status=${status}&pageSize=100`, { signal }),
  });
  const { data: scan } = useQuery({
    queryKey: ['duplicates', 'scan'],
    queryFn: ({ signal }) => api.get<DuplicateScanStatusDTO>('/api/duplicates/scan', { signal }),
    refetchInterval: (query) => (query.state.data?.running ? 2_000 : false),
  });
  const startScan = useMutation({
    mutationFn: () => api.post<DuplicateScanStatusDTO>('/api/duplicates/scan'),
    onSuccess: async (next) => {
      queryClient.setQueryData(['duplicates', 'scan'], next);
      toast.success('Scanning every lead for duplicates…');
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
  const job = scan?.job;
  const wasRunning = scan?.running;

  return (
    <Page width="wide">
      <PageHeader
        title="Duplicates"
        description="Pizzerias that look like the same business. Merge them, or mark them as different so they are not flagged again."
        actions={
          can('lead:merge') ? (
            <Button icon={ScanSearch} loading={startScan.isPending || wasRunning} onClick={() => startScan.mutate()}>
              Scan all leads
            </Button>
          ) : undefined
        }
      />
      {job && (
        <p className="mb-3 text-[13px] text-slate-600">
          {scan.running ? (
            <>
              Scanning… {job.processed} of {job.total} leads checked, {job.flagged} pairs flagged.
            </>
          ) : job.status === 'failed' ? (
            <span className="text-red-700">Last scan failed: {job.lastError}</span>
          ) : (
            <>
              Last full scan <RelativeTime value={job.completedAt ?? job.createdAt} /> — {job.total} leads checked, {job.flagged} pairs flagged.
            </>
          )}
        </p>
      )}
      <Card>
        <Tabs
          tabs={STATUS_TABS.map((tab) => ({ ...tab, count: data?.counts[tab.key] }))}
          value={status}
          onChange={setStatus}
        />
        {isLoading ? (
          <Skeleton className="m-4 h-32" />
        ) : (data?.items.length ?? 0) === 0 ? (
          <EmptyState icon={Copy} title={status === 'pending' ? 'No possible duplicates' : 'Nothing here yet'} description={status === 'pending' ? 'New and imported leads are checked automatically; run a full scan after big imports.' : undefined} />
        ) : (
          <ul className="divide-y divide-slate-100">
            {data!.items.map((pair) => (
              <li key={pair.id}>
                <Link to={`/duplicates/${pair.id}`} className="grid grid-cols-[4.5rem_minmax(0,1fr)_minmax(0,1fr)_auto] items-center gap-4 px-4 py-3 text-[13px] hover:bg-slate-50">
                  <Badge color={confidenceColor(pair.confidence)}>{pair.confidence}%</Badge>
                  <PairSide side={pair.a} />
                  <PairSide side={pair.b} />
                  <span className="text-right text-xs text-slate-500">
                    {pair.signals.slice(0, 3).map((signal) => signal.signal.replace(/_/g, ' ')).join(', ')}
                    <br />
                    <RelativeTime value={pair.resolvedAt ?? pair.detectedAt} />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </Page>
  );
}
